import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getAccessTokenSilentlyOutsideComponent } from '@/main.js'
import { useUserStore } from '@/stores/user'
import * as Ably from 'ably/promises'

export const useRoomStore = defineStore('room', () => {
  const userStore = useUserStore()
  // State
  const users = ref({})
  const round = ref({})
  const cards = ref([])
  const isRevealed = ref(false)
  const websocketChannel = ref(null)
  const roomId = ref(null)
  const canShowResetButton = ref(false)
  const votes = ref(new Map())
  const myVote = computed(() => {
    return votes.value.get(userStore.id)
  })


  const isLoadingRoom = ref(false)
  const isConnectedWithAbly = ref(false)

  const hasUserError = ref(false)
  const hasRoomError = ref(false)
  const hasAblyError = ref(false)

  const isLoading = computed(() => isLoadingRoom.value || !isConnectedWithAbly.value)

  //My user State in the room
  const isSpectating = ref(false)

  const showRevealButton = computed(() => {
    const userValues = Object.entries(users.value ?? {})
    if (votes.value.size === 0) return false

    let allUsersVotedOrSpectating = true

    userValues.forEach(([key, user]) => {
      if (votes.value.get(key) === undefined && !user.isSpectating) {
        allUsersVotedOrSpectating = false
      }
    })
    return allUsersVotedOrSpectating && !isRevealed.value
  })

  const showResetButton = computed(() => {
    return isRevealed.value && !showRevealButton.value
  })

  // Actions
  function shouldResetButtonBeVisible() {
    return isRevealed.value && !canShowResetButton.value
  }

  function setRoomData(data) {
    round.value = data.round
    isRevealed.value = !!data.isRevealed
    cards.value = data.cards
    canShowResetButton.value = isRevealed.value
    votes.value = new Map(
      Object.entries(data?.round?.votes).map(([userId, voteData]) => [userId, voteData.score])
    )

    isLoadingRoom.value = false
  }

  async function syncUsersWithPresence() {
    try {
      const presenceMembers = await websocketChannel.value.presence.get()
      const updatedUsers = {}

      presenceMembers.forEach((member) => {
        updatedUsers[member.clientId] = {
          name: member.data.name,
          id: member.clientId,
          isSpectating: member.data.isSpectating || false
        }
      })
      
      users.value = updatedUsers
    } catch (error) {
      console.error('Failed to sync users with presence:', error)
    }
  }

  async function updateUserInPresence() {
    console.log("updateUserInPresence", userStore.name, userStore.id)
    websocketChannel.value.presence.update({
      name: userStore.name,
      id: userStore.id
    })
  }

  async function createSockets(channelId, userId, userName) {
    roomId.value = channelId

    const token = await getAccessTokenSilentlyOutsideComponent()

    const ablyRealtime = new Ably.Realtime.Promise({
      authUrl: `${import.meta.env.VITE_BACKEND_BASE_URL}/rooms/${roomId.value}/generate-ably-token`,
      authHeaders: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    ablyRealtime.connection.on((stateChange) => {
      if (stateChange.current === 'connected') {
        isConnectedWithAbly.value = true
      } else {
        isConnectedWithAbly.value = false
      }
    })

    websocketChannel.value = ablyRealtime.channels.get(channelId)
    websocketChannel.value.presence.enter({
      name: userName,
      id: userId
    })

    websocketChannel.value.presence.subscribe('enter', () => {
      syncUsersWithPresence()
    })

    websocketChannel.value.presence.subscribe('leave', () => {
      syncUsersWithPresence()
    })

    websocketChannel.value.presence.subscribe('update', () => {
      syncUsersWithPresence()
    })

    websocketChannel.value?.subscribe('room_reveal', (message) => {
      round.value = message?.data?.round
      isRevealed.value = message?.data?.isRevealed
    })

    websocketChannel.value?.subscribe('room_reset', (message) => {
      round.value = message?.data?.round
      isRevealed.value = false
      votes.value = new Map()
    })

    websocketChannel.value?.subscribe('room_update', (message) => {
      console.log(message)
      round.value = {
        ...round.value,
        ...message?.data?.round
      }
      cards.value = message?.data?.cards
      votes.value = new Map(
        Object.entries(message?.data?.round?.votes).map(([userId, voteData]) => [
          userId,
          voteData.score
        ])
      )
    })

    websocketChannel.value?.subscribe('user_spectate', (message) => {
      console.log(message)
      users.value = message?.data?.users
    })
    websocketChannel.value?.subscribe('user_vote', (message) => {
      votes.value = new Map(
        Object.entries(message?.data?.round?.votes).map(([userId, voteData]) => [
          userId,
          voteData.score
        ])
      )
    })

  }

  async function onVote(cardIndex) {
    if (isSpectating.value || isRevealed.value) {
      return
    }

    const shouldRemoveVote = myVote.value === cardIndex
    const originalVote = myVote.value
    votes.value.set(userStore.user.id, shouldRemoveVote ? undefined : cardIndex)

    try {
      const token = await getAccessTokenSilentlyOutsideComponent()

      await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/rooms/${roomId.value}/vote`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          score: shouldRemoveVote ? undefined : cardIndex
        })
      })
    } catch (e) {
      votes.value.set(userStore.user.id, originalVote)
    }
  }

  function updatePresence({ id, userName, isSpectating }) {
    // Get current presence data or use defaults
    const currentData = {
      id: id,
      name: userName || users.value[id]?.name,
      isSpectating:
        isSpectating !== undefined ? isSpectating : users.value[id]?.isSpectating || false
    }

    websocketChannel.value.presence.update(currentData)
  }

  async function toggleSpectate() {
    updatePresence({
      id: userStore.id,
      isSpectating: !isSpectating.value
    })
    isSpectating.value = !isSpectating.value
  }

  async function updateCards(cards) {
    const originalCards = cards.value
    cards.value = cards

    try {
      const token = await getAccessTokenSilentlyOutsideComponent()
      await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/rooms/${roomId.value}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cards: cards
        })
      })
    } catch (e) {
      //Revert to original value as request failed
      cards.value = originalCards
    }
  }

  async function joinRoom(roomId){
    const token = await getAccessTokenSilentlyOutsideComponent()
    if(!token){
      hasUserError.value = true
      return
    }

    const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/rooms/${roomId}/join`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    })

    if(response.status !== 200){
      hasRoomError.value = true
      return
    }

    const data = await response.json()
    setRoomData(data)
    createSockets(roomId, data.user.id, data.user.userName)
  }

  function cleanup() {
    websocketChannel.value?.detach()
    websocketChannel.value = null
    roomId.value = null
    users.value = {}
    round.value = {}
    isRevealed.value = false
  }

  function resetLoadingState(){
    isLoadingRoom.value = true
    isConnectedWithAbly.value = false
  }

  return {
    // State
    users,
    round,
    cards,
    isRevealed,
    roomId,
    myVote,
    votes,
    canShowResetButton,
    isSpectating,
    isLoading,
    hasAblyError,
    hasRoomError,
    hasUserError,
    // Getters
    showRevealButton,
    showResetButton,
    // Actions
    cleanup,
    onVote,
    toggleSpectate,
    updateCards,
    shouldResetButtonBeVisible,
    resetLoadingState,
    joinRoom,
    updateUserInPresence
  }
})
